import colors from './appColors'

// ─── TypeScript Interfaces ───────────────────────────────────────────────────

export interface RoleType {
  id: string
  name: string
  colors?: { bg: string; fg: string }
}

export interface StandaloneRole extends RoleType {
  colors: { bg: string; fg: string }
}

export interface RoleGroup {
  label: string
  roles: RoleType[]
  colors: { bg: string; fg: string }
}

export interface DisplayTag {
  key: string
  label: string
  colors: { bg: string; fg: string }
  roleIds: string[]
}

// ─── Everyone Badge Colors ──────────────────────────────────────────────────

export const EVERYONE_COLORS = {
  bg: 'rgba(130,135,143,0.2)',
  fg: '#c1c2c3',
}

export const EVERYONE_COLORS_LIGHT = {
  bg: 'rgba(130,135,143,0.15)',
  fg: '#5b5d61',
}

// ─── Light-Mode Role Colors (mobile / light backgrounds) ────────────────────
// Same hues as dark-mode but with higher-opacity backgrounds and darker text
// for readability on white/light backgrounds.

const ROLE_COLORS_LIGHT: Record<string, { bg: string; fg: string }> = {
  '0': { bg: 'rgba(255,206,64,0.2)', fg: '#a97e00' }, // Owner
  '1': { bg: 'rgba(255,199,38,0.18)', fg: '#946e00' }, // Admin
  '2': { bg: 'rgba(241,212,129,0.2)', fg: '#8a6d00' }, // League Manager
  '3': { bg: 'rgba(98,185,255,0.18)', fg: '#2768b3' }, // Referee
  '4': { bg: 'rgba(81,173,246,0.18)', fg: '#225a9c' }, // Scorekeeper
  '5': { bg: 'rgba(188,55,211,0.18)', fg: '#8b1fa0' }, // Videographer
  '6': { bg: 'rgba(8,227,255,0.15)', fg: '#007a8a' }, // Player
  '7': { bg: 'rgba(255,173,51,0.18)', fg: '#b38705' }, // Team Official
  '8': { bg: 'rgba(81,173,246,0.18)', fg: '#225a9c' }, // Timekeeper
  '9': { bg: 'rgba(110,192,113,0.18)', fg: '#0b7923' }, // Followers
}

// Group-level light colors (used when showing collapsed group badges)
const GROUP_COLORS_LIGHT: Record<string, { bg: string; fg: string }> = {
  Admins: { bg: 'rgba(255,199,38,0.18)', fg: '#946e00' },
  Officials: { bg: 'rgba(98,185,255,0.18)', fg: '#2768b3' },
}

// ─── Standalone Roles ────────────────────────────────────────────────────────

export const STANDALONE_ROLES_TOP: StandaloneRole[] = [
  {
    id: '9',
    name: 'Followers',
    colors: { bg: 'rgba(110,192,113,0.15)', fg: '#6ec071' },
  },
  {
    id: '6',
    name: 'Player',
    colors: { bg: 'rgba(8,227,255,0.15)', fg: '#08e3ff' },
  },
  {
    id: '7',
    name: 'Team Official',
    colors: { bg: 'rgba(255,173,51,0.1)', fg: '#ffad33' },
  },
]

export const STANDALONE_ROLES_BOTTOM: StandaloneRole[] = [
  {
    id: '5',
    name: 'Videographer',
    colors: { bg: 'rgba(231,92,255,0.15)', fg: '#E75CFF' },
  },
]

export const STANDALONE_ROLES: StandaloneRole[] = [
  ...STANDALONE_ROLES_TOP,
  ...STANDALONE_ROLES_BOTTOM,
]

// ─── Role Groups ─────────────────────────────────────────────────────────────

export const ROLE_GROUPS: RoleGroup[] = [
  {
    label: 'Admins',
    roles: [
      {
        id: '0',
        name: 'Owner',
        colors: { bg: 'rgba(255,206,64,0.2)', fg: '#ffce40' },
      },
      {
        id: '1',
        name: 'Admin',
        colors: { bg: 'rgba(255,199,38,0.15)', fg: '#ffc726' },
      },
      {
        id: '2',
        name: 'League Manager',
        colors: { bg: 'rgba(241, 212, 129, 0.10)', fg: '#f1d481' },
      },
    ],
    colors: { bg: 'rgba(255,199,38,0.15)', fg: '#ffc726' },
  },
  {
    label: 'Officials',
    roles: [
      {
        id: '3',
        name: 'Referee',
        colors: { bg: 'rgba(98,185,255,0.2)', fg: '#62b9ff' },
      },
      {
        id: '4',
        name: 'Scorekeeper',
        colors: { bg: 'rgba(81,173,246,0.15)', fg: '#51adf6' },
      },
      {
        id: '8',
        name: 'Timekeeper',
        colors: { bg: 'rgba(81,173,246,0.15)', fg: '#51adf6' },
      },
    ],
    colors: { bg: 'rgba(98,185,255,0.2)', fg: '#62b9ff' },
  },
]

// ─── Flat Lookup Map ─────────────────────────────────────────────────────────

export const ALL_ROLES: Record<string, RoleType> = {}
STANDALONE_ROLES.forEach((r) => (ALL_ROLES[r.id] = r))
ROLE_GROUPS.forEach((g) => g.roles.forEach((r) => (ALL_ROLES[r.id] = r)))

export const TOTAL_ROLE_COUNT = 10

// ─── Utilities ───────────────────────────────────────────────────────────────

/**
 * Returns background and foreground colors for a given role id,
 * checking per-role colors first, then falling back to group colors.
 */
export const getRoleColors = (
  roleId: number | string
): { bg: string; fg: string } => {
  const idStr = String(roleId)

  const standalone = STANDALONE_ROLES.find((r) => r.id === idStr)
  if (standalone) {
    return standalone.colors
  }

  for (const group of ROLE_GROUPS) {
    const role = group.roles.find((r) => r.id === idStr)
    if (role) {
      return role.colors || group.colors
    }
  }

  return { bg: colors.NEUTRAL_20, fg: colors.NEUTRAL_500 }
}

/**
 * Returns light-mode colors for a given role id.
 * Higher-opacity backgrounds and darker text for readability on white/light backgrounds.
 */
export const getRoleColorsLight = (
  roleId: number | string
): { bg: string; fg: string } => {
  const idStr = String(roleId)
  return (
    ROLE_COLORS_LIGHT[idStr] || {
      bg: colors.NEUTRAL_20,
      fg: colors.NEUTRAL_500,
    }
  )
}

/**
 * Returns light-mode colors for a group label.
 */
export const getGroupColorsLight = (
  groupLabel: string
): { bg: string; fg: string } => {
  return (
    GROUP_COLORS_LIGHT[groupLabel] || {
      bg: colors.NEUTRAL_20,
      fg: colors.NEUTRAL_500,
    }
  )
}

/**
 * Builds smart display tags from a list of selected role IDs.
 * - Groups all selected roles in a group into a single group tag
 * - Shows individual role tags for ungrouped selections
 */
export const buildDisplayTags = (selectedRoleIds: string[]): DisplayTag[] => {
  if (selectedRoleIds.length === 0) return []

  // If all roles are selected, show a single "Everyone" tag
  const allIds = Object.keys(ALL_ROLES)
  if (allIds.length > 0 && allIds.every((id) => selectedRoleIds.includes(id))) {
    return [
      {
        key: 'everyone',
        label: 'Everyone',
        colors: EVERYONE_COLORS,
        roleIds: allIds,
      },
    ]
  }

  const tags: DisplayTag[] = []
  const coveredIds = new Set<string>()

  for (const group of ROLE_GROUPS) {
    const groupIds = group.roles.map((r) => r.id)
    const allSelected = groupIds.every((id) => selectedRoleIds.includes(id))
    if (allSelected) {
      tags.push({
        key: `group-${group.label}`,
        label: group.label,
        colors: group.colors,
        roleIds: groupIds,
      })
      groupIds.forEach((id) => coveredIds.add(id))
    }
  }

  for (const id of selectedRoleIds) {
    if (!coveredIds.has(id)) {
      const roleColors = getRoleColors(id)
      const role = ALL_ROLES[id]
      if (role) {
        tags.push({
          key: id,
          label: role.name,
          colors: roleColors,
          roleIds: [id],
        })
      }
    }
  }

  return tags
}

/**
 * Light-mode variant of buildDisplayTags for mobile / light backgrounds.
 */
export const buildDisplayTagsLight = (
  selectedRoleIds: string[]
): DisplayTag[] => {
  if (selectedRoleIds.length === 0) return []

  // If all roles are selected, show a single "Everyone" tag
  const allIds = Object.keys(ALL_ROLES)
  if (allIds.length > 0 && allIds.every((id) => selectedRoleIds.includes(id))) {
    return [
      {
        key: 'everyone',
        label: 'Everyone',
        colors: EVERYONE_COLORS_LIGHT,
        roleIds: allIds,
      },
    ]
  }

  const tags: DisplayTag[] = []
  const coveredIds = new Set<string>()

  for (const group of ROLE_GROUPS) {
    const groupIds = group.roles.map((r) => r.id)
    const allSelected = groupIds.every((id) => selectedRoleIds.includes(id))
    if (allSelected) {
      tags.push({
        key: `group-${group.label}`,
        label: group.label,
        colors: getGroupColorsLight(group.label),
        roleIds: groupIds,
      })
      groupIds.forEach((id) => coveredIds.add(id))
    }
  }

  for (const id of selectedRoleIds) {
    if (!coveredIds.has(id)) {
      const role = ALL_ROLES[id]
      if (role) {
        tags.push({
          key: id,
          label: role.name,
          colors: getRoleColorsLight(id),
          roleIds: [id],
        })
      }
    }
  }

  return tags
}
